import React from "react";
import { TREATMENT_CHECKOUT_COPY } from "./treatmentUiConstants";
import "./treatmentCheckoutJourney.css";

const money = (value, currency = "USD") => {
  const amount = Number(value || 0);
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
    }).format(Number.isFinite(amount) ? amount : 0);
  } catch (_error) {
    return `$${(Number.isFinite(amount) ? amount : 0).toFixed(2)}`;
  }
};

const positive = (value) => Number(value || 0) > 0;

function ProductLine({ product, currency }) {
  return (
    <div className="treatment-checkout__product">
      <div className="treatment-checkout__product-copy">
        <strong>{product.name}</strong>
        <span>
          {product.days_supply ? `${product.days_supply}-day supply` : "Treatment"}
          {product.quantity > 1 ? ` · Qty ${product.quantity}` : ""}
        </span>
      </div>
      <strong>{money(product.line_total, currency)}</strong>
      {product.linked_supplies?.length > 0 && (
        <div className="treatment-checkout__supplies">
          <span>{TREATMENT_CHECKOUT_COPY.supplies}</span>
          {product.linked_supplies.map((supply) => (
            <div key={supply.id || supply.name}>
              <span>
                {supply.name}
                {supply.quantity > 1 ? ` · Qty ${supply.quantity}` : ""}
              </span>
              <strong>
                {supply.is_included
                  ? "Included"
                  : money(supply.line_total, currency)}
              </strong>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LabLines({ labs, currency }) {
  if (!labs?.length) return null;
  return (
    <div className="treatment-checkout__labs">
      <div className="treatment-checkout__section-label">
        {TREATMENT_CHECKOUT_COPY.labs}
      </div>
      {labs.map((lab) => (
        <div className="treatment-checkout__lab" key={lab.id || lab.name}>
          <div>
            <strong>{lab.name}</strong>
            <span>{lab.required ? "Required for this treatment" : "Optional lab"}</span>
          </div>
          <strong>{money(lab.line_total, currency)}</strong>
        </div>
      ))}
    </div>
  );
}

function Totals({ treatment, currency }) {
  return (
    <dl className="treatment-checkout__totals">
      <div>
        <dt>{TREATMENT_CHECKOUT_COPY.subtotal}</dt>
        <dd>{money(treatment.product_subtotal, currency)}</dd>
      </div>
      {positive(treatment.product_shipping) && (
        <div>
          <dt>{TREATMENT_CHECKOUT_COPY.shipping}</dt>
          <dd>{money(treatment.product_shipping, currency)}</dd>
        </div>
      )}
      {positive(treatment.product_discount) && (
        <div className="is-discount">
          <dt>{TREATMENT_CHECKOUT_COPY.discount}</dt>
          <dd>−{money(treatment.product_discount, currency)}</dd>
        </div>
      )}
      {positive(treatment.lab_total) && (
        <div>
          <dt>Labs</dt>
          <dd>{money(treatment.lab_total, currency)}</dd>
        </div>
      )}
      <div className="is-total">
        <dt>Treatment total</dt>
        <dd>{money(treatment.total, currency)}</dd>
      </div>
    </dl>
  );
}

export default function TreatmentCheckoutReview({
  summary,
  children,
  previewMode = false,
  previewDiscountRate = 0,
}) {
  if (!previewMode) {
    return <>{children}</>;
  }

  const currency = summary?.currency || "USD";
  const treatments = summary?.treatments || [];
  const discountRate = Math.min(
    1,
    Math.max(0, Number(previewDiscountRate) || 0),
  );
  const baseTotal = Math.max(0, Number(summary?.grand_total || 0));
  const discountAmount = Number((baseTotal * discountRate).toFixed(2));
  const dueToday = Math.max(
    0,
    Number((baseTotal - discountAmount).toFixed(2)),
  );
  const hasPreviewDiscount = discountAmount > 0;
  const discountPercent = Math.round(discountRate * 100);

  return (
    <section className="treatment-checkout">
      <header className="treatment-checkout__intro">
        <span>Checkout</span>
        <h1>{TREATMENT_CHECKOUT_COPY.title}</h1>
        <p>{TREATMENT_CHECKOUT_COPY.description}</p>
      </header>

      <div className="treatment-checkout__cards">
        {treatments.map((treatment) => (
          <article
            className="treatment-checkout__card"
            key={treatment.treatment_type_id || treatment.program_id}
          >
            <header>
              <span>{TREATMENT_CHECKOUT_COPY.products}</span>
              <h2>{treatment.name}</h2>
            </header>
            <div className="treatment-checkout__products">
              {treatment.products.map((product) => (
                <ProductLine
                  product={product}
                  currency={currency}
                  key={product.option_id || product.id}
                />
              ))}
            </div>
            <LabLines labs={treatment.labs} currency={currency} />
            <Totals treatment={treatment} currency={currency} />
          </article>
        ))}
      </div>

      {hasPreviewDiscount ? (
        <div className="treatment-checkout__grand-total-stack">
          <div className="treatment-checkout__grand-total treatment-checkout__grand-total--base">
            <span>Treatment total</span>
            <strong>{money(baseTotal, currency)}</strong>
          </div>
          <div className="treatment-checkout__grand-discount">
            <span>{`Discount · ${discountPercent}% off`}</span>
            <strong>−{money(discountAmount, currency)}</strong>
          </div>
          <div className="treatment-checkout__grand-total treatment-checkout__grand-total--due">
            <span>{TREATMENT_CHECKOUT_COPY.total}</span>
            <strong>{money(dueToday, currency)}</strong>
          </div>
        </div>
      ) : (
        <div className="treatment-checkout__grand-total">
          <span>{TREATMENT_CHECKOUT_COPY.total}</span>
          <strong>{money(baseTotal, currency)}</strong>
        </div>
      )}
      <div className="treatment-checkout__authorization" role="note">
        <span aria-hidden="true">i</span>
        <div>
          <strong>{TREATMENT_CHECKOUT_COPY.authorizationHeading}</strong>
          <p>{TREATMENT_CHECKOUT_COPY.authorization}</p>
          <ul>
            {TREATMENT_CHECKOUT_COPY.authorizationSteps.map((step) => (
              <li key={step}>{step}</li>
            ))}
            {summary?.labs?.length > 0 && (
              <li>{TREATMENT_CHECKOUT_COPY.labAuthorization}</li>
            )}
          </ul>
        </div>
      </div>
      <div className="treatment-checkout__payment">
        <h2>{TREATMENT_CHECKOUT_COPY.paymentHeading}</h2>
        {children}
        <p className="treatment-checkout__compliance">
          {TREATMENT_CHECKOUT_COPY.compliance}
        </p>
      </div>
    </section>
  );
}
